<template>
  <div class="reply-box" :class="replyBoxClass">
    <banner
      v-if="showSelfAssignBanner"
      action-button-variant="clear"
      color-scheme="secondary"
      class="banner--self-assign"
      :banner-message="$t('CONVERSATION.NOT_ASSIGNED_TO_YOU')"
      :has-action-button="true"
      :action-button-label="$t('CONVERSATION.ASSIGN_TO_ME')"
      @click="onClickSelfAssign"
    />
    <reply-top-panel
      :mode="replyType"
      :set-reply-mode="setReplyMode"
      :is-message-length-reaching-threshold="isMessageLengthReachingThreshold"
      :characters-remaining="charactersRemaining"
      :popout-reply-box="popoutReplyBox"
      @click="$emit('click')"
    />
    <div class="reply-box__top">
      <canned-response
        v-if="showMentions && hasSlashCommand"
        v-on-clickaway="hideMentions"
        class="normal-editor__canned-box"
        :search-key="mentionSearchKey"
        @click="replaceText"
      />
      <emoji-input
        v-if="showEmojiPicker"
        v-on-clickaway="hideEmojiPicker"
        :class="emojiDialogClassOnExpandedLayoutAndRTLView"
        :on-click="emojiOnClick"
      />
      <reply-email-head
        v-if="showReplyHead"
        :cc-emails.sync="ccEmails"
        :bcc-emails.sync="bccEmails"
        :to-emails.sync="toEmails"
      />
      <woot-audio-recorder
        v-if="showAudioRecorderEditor"
        ref="audioRecorderInput"
        :audio-record-format="audioRecordFormat"
        @state-recorder-progress-changed="onStateProgressRecorderChanged"
        @state-recorder-changed="onStateRecorderChanged"
        @finish-record="onFinishRecorder"
      />
      <resizable-text-area
        v-else-if="!showRichContentEditor"
        ref="messageInput"
        v-model.trim="message"
        class="input"
        :placeholder="messagePlaceHolder"
        :min-height="4"
        @typing-off="onTypingOff"
        @typing-on="onTypingOn"
        @focus="onFocus"
        @blur="onBlur"
      />
      <woot-message-editor
        v-else
        ref="messageEditor"
        v-model.trim="message"
        :editor-id="editorStateId"
        class="input"
        :is-private="isOnPrivateNote"
        :placeholder="messagePlaceHolder"
        :update-selection-with="updateEditorSelectionWith"
        :min-height="4"
        :enable-variables="true"
        :variables="messageVariables"
        @typing-off="onTypingOff"
        @typing-on="onTypingOn"
        @focus="onFocus"
        @blur="onBlur"
        @toggle-user-mention="toggleUserMention"
        @toggle-canned-menu="toggleCannedMenu"
        @toggle-variables-menu="toggleVariablesMenu"
        @clear-selection="clearEditorSelection"
        @image-upload="onEditorImageUpload"
        @non-image-drop="onEditorNonImageDrop"
      />
    </div>
    <div v-if="activeUploads.length" class="upload-progress-box">
      <div
        v-for="upload in activeUploads"
        :key="upload.id"
        class="upload-progress-item"
      >
        <div class="upload-thumb-wrap">
          <img
            v-if="upload.thumb"
            class="upload-thumb"
            :src="upload.thumb"
            alt=""
          />
          <span v-else class="upload-thumb-icon">📄</span>
        </div>
        <div class="upload-meta">
          <div class="upload-name" :title="upload.name">{{ upload.name }}</div>
          <div class="upload-bar">
            <div
              class="upload-bar-fill"
              :style="{ width: upload.progress + '%' }"
            />
          </div>
        </div>
        <div class="upload-percent">{{ upload.progress }}%</div>
      </div>
    </div>
    <div
      v-if="hasVisibleAttachments"
      class="attachment-preview-box"
      @paste="onPaste"
    >
      <attachment-preview
        :attachments="visibleAttachedFiles"
        :remove-attachment="removeAttachment"
      />
    </div>
    <div
      v-if="isSignatureEnabledForInbox"
      v-tooltip="$t('CONVERSATION.FOOTER.MESSAGE_SIGN_TOOLTIP')"
      class="message-signature-wrap"
    >
      <p
        v-if="isSignatureAvailable"
        v-dompurify-html="formatMessage(messageSignature)"
        class="message-signature"
      />
      <p v-else class="message-signature">
        {{ $t('CONVERSATION.FOOTER.MESSAGE_SIGNATURE_NOT_CONFIGURED') }}
        <router-link :to="profilePath">
          {{ $t('CONVERSATION.FOOTER.CLICK_HERE') }}
        </router-link>
      </p>
    </div>
    <reply-bottom-panel
      ref="bottomPanel"
      :conversation-id="conversationId"
      :enable-multiple-file-upload="enableMultipleFileUpload"
      :has-whatsapp-templates="hasWhatsappTemplates"
      :inbox="inbox"
      :is-on-private-note="isOnPrivateNote"
      :is-recording-audio="isRecordingAudio"
      :is-send-disabled="isReplyButtonDisabled"
      :mode="replyType"
      :on-file-upload="onFileUpload"
      :on-send="onSendReply"
      :recording-audio-duration-text="recordingAudioDurationText"
      :recording-audio-state="recordingAudioState"
      :send-button-text="replyButtonLabel"
      :show-audio-recorder="showAudioRecorder"
      :show-editor-toggle="isAPIInbox && !isOnPrivateNote"
      :show-emoji-picker="showEmojiPicker"
      :show-file-upload="showFileUpload"
      :toggle-audio-recorder-play-pause="toggleAudioRecorderPlayPause"
      :toggle-audio-recorder="toggleAudioRecorder"
      :toggle-emoji-picker="toggleEmojiPicker"
      :message="message"
      @selectWhatsappTemplate="openWhatsappTemplateModal"
      @selectEmailTemplate="openEmailTemplateModal"
      @toggle-editor="toggleRichContentEditor"
      @replace-text="replaceText"
      @suggestion-generated="handleSuggestionGenerated"
      @send-agent-content="handleSendAgentContent"
      @open-macros-panel="$emit('open-macros-panel')"
    />
    <whatsapp-templates
      :inbox-id="inbox.id"
      :show="showWhatsAppTemplatesModal"
      @close="hideWhatsappTemplatesModal"
      @on-send="onSendWhatsAppReply"
      @cancel="hideWhatsappTemplatesModal"
    />
    <email-templates
      :show="showEmailTemplatesModal"
      @on-send="onSendEmailTemplate"
      @cancel="hideEmailTemplateModal"
    />

    <woot-confirm-modal
      ref="confirmDialog"
      :title="$t('CONVERSATION.REPLYBOX.UNDEFINED_VARIABLES.TITLE')"
      :description="undefinedVariableMessage"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { mixin as clickaway } from 'vue-clickaway';
import alertMixin from 'shared/mixins/alertMixin';

import CannedResponse from './CannedResponse';
import ResizableTextArea from 'shared/components/ResizableTextArea';
import AttachmentPreview from 'dashboard/components/widgets/AttachmentsPreview';
import ReplyTopPanel from 'dashboard/components/widgets/WootWriter/ReplyTopPanel';
import ReplyEmailHead from './ReplyEmailHead';
import ReplyBottomPanel from 'dashboard/components/widgets/WootWriter/ReplyBottomPanel';
import Banner from 'dashboard/components/ui/Banner.vue';
import { REPLY_EDITOR_MODES } from 'dashboard/components/widgets/WootWriter/constants';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor';
import {getMessageVariables, getUndefinedVariablesInMessage, replaceVariablesInMessage} from 'dashboard/helper/messageHelper';
import WootAudioRecorder from 'dashboard/components/widgets/WootWriter/AudioRecorder';
import messageFormatterMixin from 'shared/mixins/messageFormatterMixin';
import { checkFileSizeLimit } from 'shared/helpers/FileHelper';
import {
  MAXIMUM_FILE_UPLOAD_SIZE,
  MAXIMUM_FILE_UPLOAD_SIZE_TWILIO_SMS_CHANNEL,
  MAXIMUM_FILE_UPLOAD_SIZE_EMAIL_CHANNEL,
  AUDIO_FORMATS,
} from 'shared/constants/messages';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import WhatsappTemplates from './WhatsappTemplates/Modal.vue';
import EmailTemplates from './EmailTemplates/Modal.vue';
import { buildHotKeys } from 'shared/helpers/KeyboardHelpers';
import { MESSAGE_MAX_LENGTH } from 'shared/helpers/MessageTypeHelper';
import inboxMixin from 'shared/mixins/inboxMixin';
import uiSettingsMixin from 'dashboard/mixins/uiSettings';
import { DirectUpload } from 'activestorage';
import { frontendURL } from '../../../helper/URLHelper';
import { trimContent, debounce } from '@chatwoot/utils';
import wootConstants from 'dashboard/constants/globals';
import { isEditorHotKeyEnabled } from 'dashboard/mixins/uiSettings';
import { CONVERSATION_EVENTS } from '../../../helper/AnalyticsHelper/events';
import rtlMixin from 'shared/mixins/rtlMixin';
import InboxesAPI from '../../../api/inboxes';

const EmojiInput = () => import('shared/components/emoji/EmojiInput');

export default {
  components: {
    EmojiInput,
    CannedResponse,
    ResizableTextArea,
    AttachmentPreview,
    ReplyTopPanel,
    ReplyEmailHead,
    ReplyBottomPanel,
    WootMessageEditor,
    WootAudioRecorder,
    Banner,
    WhatsappTemplates,
    EmailTemplates,
  },
  mixins: [
    clickaway,
    inboxMixin,
    uiSettingsMixin,
    alertMixin,
    messageFormatterMixin,
    rtlMixin,
  ],
  props: {
    selectedTweet: {
      type: [Object, String],
      default: () => ({}),
    },
    isATweet: {
      type: Boolean,
      default: false,
    },
    popoutReplyBox: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      message: '',
      isFocused: false,
      showEmojiPicker: false,
      attachedFiles: [],
      isRecordingAudio: false,
      recordingAudioState: '',
      recordingAudioDurationText: '',
      isUploading: false,
      replyType: REPLY_EDITOR_MODES.REPLY,
      mentionSearchKey: '',
      hasSlashCommand: false,
      bccEmails: '',
      ccEmails: '',
      toEmails: '',
      doAutoSaveDraft: () => {},
      showWhatsAppTemplatesModal: false,
      showEmailTemplatesModal: false,
      updateEditorSelectionWith: '',
      undefinedVariableMessage: '',
      showMentions: false,
      showUserMentions: false,
      showCannedMenu: false,
      showVariablesMenu: false,
      typingInterval: null,
      lastWhatsappTypingSent: null,
      whatsappTypingDebounceTimer: null,
      isWhatsappTypingActive: false,
      whatsappTypingTimeout: null,
      activeUploads: [],
    };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      messageSignature: 'getEmailMessageSignature',
      currentUser: 'getCurrentUser',
      lastEmail: 'getLastEmailInSelectedChat',
      globalConfig: 'globalConfig/get',
      accountId: 'getCurrentAccountId',
    }),
    showRichContentEditor() {
      if (this.isOnPrivateNote || this.isRichEditorEnabled) {
        return true;
      }

      if (this.isAPIInbox) {
        const {
          display_rich_content_editor: displayRichContentEditor = false,
        } = this.uiSettings;
        return displayRichContentEditor;
      }

      return false;
    },
    assignedAgent: {
      get() {
        return this.currentChat.meta.assignee;
      },
      set(agent) {
        const agentId = agent ? agent.id : 0;
        this.$store
          .dispatch('assignAgent', {
            conversationId: this.currentChat.id,
            agentId,
          })
          .then(() => {
            this.showAlert(this.$t('CONVERSATION.CHANGE_AGENT'));
          })
          .catch(error => {
            const message =
              error?.response?.data?.error ||
              this.$t('CONVERSATION.CHANGE_AGENT_FAILED');
            this.showAlert(message);
          });
      },
    },
    showSelfAssignBanner() {
      if (this.message !== '' && !this.isOnPrivateNote) {
        if (!this.assignedAgent) {
          return true;
        }
        if (this.assignedAgent.id !== this.currentUser.id) {
          return true;
        }
      }

      return false;
    },
    hasWhatsappTemplates() {
      return !!this.$store.getters['inboxes/getWhatsAppTemplates'](this.inboxId)
        .length;
    },
    isPrivate() {
      if (this.currentChat.can_reply || this.isAWhatsAppChannel) {
        return this.isOnPrivateNote;
      }
      return true;
    },
    inboxId() {
      return this.currentChat.inbox_id;
    },
    inbox() {
      return this.$store.getters['inboxes/getInbox'](this.inboxId);
    },
    messagePlaceHolder() {
      return this.isPrivate
        ? this.$t('CONVERSATION.FOOTER.PRIVATE_MSG_INPUT')
        : this.$t('CONVERSATION.FOOTER.MSG_INPUT');
    },
    isMessageLengthReachingThreshold() {
      return this.message.length > this.maxLength - 50;
    },
    charactersRemaining() {
      return this.maxLength - this.message.length;
    },
    isReplyButtonDisabled() {
      if (this.isATweet && !this.inReplyTo && !this.isOnPrivateNote) {
        return true;
      }

      if (this.hasAttachments || this.hasRecordedAudio) return false;

      return (
        this.isMessageEmpty ||
        this.message.length === 0 ||
        this.message.length > this.maxLength
      );
    },
    sender() {
      return {
        name: this.currentUser.name,
        thumbnail: this.currentUser.avatar_url,
      };
    },
    conversationType() {
      const { additional_attributes: additionalAttributes } = this.currentChat;
      const type = additionalAttributes ? additionalAttributes.type : '';
      return type || '';
    },
    maxLength() {
      if (this.isPrivate) {
        return MESSAGE_MAX_LENGTH.GENERAL;
      }
      if (this.isAFacebookInbox) {
        return MESSAGE_MAX_LENGTH.FACEBOOK;
      }
      if (this.isAWhatsAppChannel) {
        return MESSAGE_MAX_LENGTH.TWILIO_WHATSAPP;
      }
      if (this.isASmsInbox) {
        return MESSAGE_MAX_LENGTH.TWILIO_SMS;
      }
      if (this.isATwitterInbox) {
        if (this.conversationType === 'tweet') {
          return MESSAGE_MAX_LENGTH.TWEET - this.replyToUserLength - 2;
        }
      }
      return MESSAGE_MAX_LENGTH.GENERAL;
    },
    showFileUpload() {
      return (
        this.isAWebWidgetInbox ||
        this.isAFacebookInbox ||
        this.isAWhatsAppChannel ||
        this.isAPIInbox ||
        this.isAnEmailChannel ||
        this.isASmsInbox ||
        this.isATelegramChannel
      );
    },
    replyButtonLabel() {
      let sendMessageText = this.$t('CONVERSATION.REPLYBOX.SEND');
      if (this.isPrivate) {
        sendMessageText = this.$t('CONVERSATION.REPLYBOX.CREATE');
      } else if (this.conversationType === 'tweet') {
        sendMessageText = this.$t('CONVERSATION.REPLYBOX.TWEET');
      }
      const keyLabel = isEditorHotKeyEnabled(this.uiSettings, 'cmd_enter')
        ? '(⌘ + ↵)'
        : '(↵)';
      return `${sendMessageText} ${keyLabel}`;
    },
    replyBoxClass() {
      return {
        'is-private': this.isPrivate,
        'is-focused': this.isFocused || this.hasAttachments,
      };
    },
    hasAttachments() {
      return this.attachedFiles.length;
    },
    visibleAttachedFiles() {
      // Inline images live in the editor body; hide their chip from the row.
      return this.attachedFiles.filter(item => !item.inline);
    },
    hasVisibleAttachments() {
      return this.visibleAttachedFiles.length > 0;
    },
    hasRecordedAudio() {
      return (
        this.$refs.audioRecorderInput &&
        this.$refs.audioRecorderInput.hasAudio()
      );
    },
    isRichEditorEnabled() {
      return this.isAWebWidgetInbox || this.isAnEmailChannel;
    },
    showAudioRecorder() {
      return !this.isOnPrivateNote && this.showFileUpload;
    },
    showAudioRecorderEditor() {
      return this.showAudioRecorder && this.isRecordingAudio;
    },
    isOnPrivateNote() {
      return this.replyType === REPLY_EDITOR_MODES.NOTE;
    },
    inReplyTo() {
      const selectedTweet = this.selectedTweet || {};
      return selectedTweet.id;
    },
    isOnExpandedLayout() {
      const {
        LAYOUT_TYPES: { CONDENSED },
      } = wootConstants;
      const {
        conversation_display_type: conversationDisplayType = CONDENSED,
      } = this.uiSettings;
      return conversationDisplayType !== CONDENSED;
    },
    emojiDialogClassOnExpandedLayoutAndRTLView() {
      if (this.isOnExpandedLayout || this.popoutReplyBox) {
        return 'emoji-dialog--expanded';
      }
      if (this.isRTLView) {
        return 'emoji-dialog--rtl';
      }
      return '';
    },
    replyToUserLength() {
      const selectedTweet = this.selectedTweet || {};
      const {
        sender: {
          additional_attributes: { screen_name: screenName = '' } = {},
        } = {},
      } = selectedTweet;
      return screenName ? screenName.length : 0;
    },
    isMessageEmpty() {
      if (!this.message) {
        return true;
      }
      return !this.message.trim().replace(/\n/g, '').length;
    },
    showReplyHead() {
      return !this.isOnPrivateNote && this.isAnEmailChannel;
    },
    enableMultipleFileUpload() {
      return (
        this.isAnEmailChannel ||
        this.isAWebWidgetInbox ||
        this.isAPIInbox ||
        this.isAWhatsAppChannel
      );
    },
    // Per-channel cap (MB). Email is tighter so the MIME envelope stays
    // under provider limits after base64 overhead.
    maxFileUploadSize() {
      if (this.isATwilioSMSChannel) return MAXIMUM_FILE_UPLOAD_SIZE_TWILIO_SMS_CHANNEL;
      if (this.isAnEmailChannel) return MAXIMUM_FILE_UPLOAD_SIZE_EMAIL_CHANNEL;
      return MAXIMUM_FILE_UPLOAD_SIZE;
    },
    isSignatureEnabledForInbox() {
      return !this.isPrivate && this.isAnEmailChannel && this.sendWithSignature;
    },
    isSignatureAvailable() {
      return !!this.messageSignature;
    },
    sendWithSignature() {
      const { send_with_signature: isEnabled } = this.uiSettings;
      return isEnabled;
    },
    profilePath() {
      return frontendURL(`accounts/${this.accountId}/profile/settings`);
    },
    editorMessageKey() {
      const { editor_message_key: isEnabled } = this.uiSettings;
      return isEnabled;
    },
    commandPlusEnterToSendEnabled() {
      return this.editorMessageKey === 'cmd_enter';
    },
    enterToSendEnabled() {
      return this.editorMessageKey === 'enter';
    },
    conversationId() {
      return this.currentChat.id;
    },
    conversationIdByRoute() {
      return this.conversationId;
    },
    editorStateId() {
      return `draft-${this.conversationIdByRoute}-${this.replyType}`;
    },
    audioRecordFormat() {
      if (this.isAWhatsAppChannel || this.isAPIInbox) {
        return AUDIO_FORMATS.OGG;
      }
      return AUDIO_FORMATS.WAV;
    },
    messageVariables() {
      const variables = getMessageVariables({
        conversation: this.currentChat,
      });
      return variables;
    },
  },
  watch: {
    currentChat(conversation) {
      const { can_reply: canReply } = conversation;

      if (this.isOnPrivateNote) {
        return;
      }

      if (canReply || this.isAWhatsAppChannel) {
        this.replyType = REPLY_EDITOR_MODES.REPLY;
      } else {
        this.replyType = REPLY_EDITOR_MODES.NOTE;
      }

      this.setCCAndToEmailsFromLastChat();
    },
    conversationIdByRoute(conversationId, oldConversationId) {
      if (conversationId !== oldConversationId) {
        this.setToDraft(oldConversationId, this.replyType);
        this.getFromDraft();
        this.resetAttachmentsOnConversationChange();
      }
    },
    message(updatedMessage) {
      const conversationId=this.conversationId;
      // bus.$emit('typing',conversationId);
      this.hasSlashCommand =
        updatedMessage[0] === '/' && !this.showRichContentEditor;
      const hasNextWord = updatedMessage.includes(' ');
      const isShortCodeActive = this.hasSlashCommand && !hasNextWord;
      if (isShortCodeActive) {
        this.mentionSearchKey = updatedMessage.substring(1);
        this.showMentions = true;
      } else {
        this.mentionSearchKey = '';
        this.showMentions = false;
      }
      this.doAutoSaveDraft();
      this.cleanupOrphanedInlineImages(updatedMessage);
    },
    replyType(updatedReplyType, oldReplyType) {
      this.setToDraft(this.conversationIdByRoute, oldReplyType);
      this.getFromDraft();
    },
  },
  mounted() {
    bus.$on('insert-message-to-reply-box', this.insertMessageToReplyBox);
    this.getFromDraft();
    this.attachGlobalListeners();
    this.setCCAndToEmailsFromLastChat();
    this.doAutoSaveDraft = debounce(
      () => {
        this.saveDraft(this.conversationIdByRoute, this.replyType);
      },
      500,
      true
    );
  },
  // Re-attach on keep-alive reactivation; detach on deactivation so a
  // backgrounded ReplyBox cannot intercept drops aimed at other dropzones.
  activated() {
    this.attachGlobalListeners();
  },
  deactivated() {
    this.detachGlobalListeners();
  },
  destroyed() {
    bus.$off('insert-message-to-reply-box', this.insertMessageToReplyBox);
    this.detachGlobalListeners();
    
    // Clean up WhatsApp typing timers
    if (this.whatsappTypingTimeout) {
      clearTimeout(this.whatsappTypingTimeout);
    }

    this.activeUploads.forEach(u => {
      if (u.thumb && u.thumb.indexOf('blob:') === 0) {
        try {
          URL.revokeObjectURL(u.thumb);
        } catch (e) {
          console.error('Error revoking object URL:', e);
        }
      }
    });
  },
  methods: {
    debouncedToggleTyping: debounce(function (status, this_info) {
      const conversationId = this_info.currentChat.id;
      const isPrivate = this_info.isPrivate;

      console.log('[Typing] Status:', status, 'ConversationId:', conversationId, 'IsPrivate:', isPrivate);

      if (!conversationId) {
        return;
      }

      this_info.$store.dispatch('conversationTypingStatus/toggleTyping', {
        status,
        conversationId,
        isPrivate,
      });

      // Handle WhatsApp typing indicator based on status
      if (status === 'on' && !isPrivate) {
        console.log('[Typing] Starting WhatsApp typing indicator');
        this_info.startWhatsappTyping();
      } else if (status === 'off') {
        console.log('[Typing] Stopping WhatsApp typing indicator');
        this_info.stopWhatsappTyping();
      }
    }, 500),
    toggleRichContentEditor() {
      this.updateUISettings({
        display_rich_content_editor: !this.showRichContentEditor,
      });
    },
    saveDraft(conversationId, replyType) {
      if (this.message || this.message === '') {
        const key = `draft-${conversationId}-${replyType}`;
        const draftToSave = trimContent(this.message || '');

        this.$store.dispatch('draftMessages/set', {
          key,
          message: draftToSave,
        });
      }
    },
    setToDraft(conversationId, replyType) {
      this.saveDraft(conversationId, replyType);
      this.message = '';
    },
    getFromDraft() {
      if (this.conversationIdByRoute) {
        const key = `draft-${this.conversationIdByRoute}-${this.replyType}`;
        this.message = this.$store.getters['draftMessages/get'](key) || '';
      }
    },
    removeFromDraft() {
      if (this.conversationIdByRoute) {
        const key = `draft-${this.conversationIdByRoute}-${this.replyType}`;
        this.$store.dispatch('draftMessages/delete', { key });
      }
    },
    handleKeyEvents(e) {
      const keyCode = buildHotKeys(e);
      if (keyCode === 'escape') {
        this.hideEmojiPicker();
        this.hideMentions();
      } else if (keyCode === 'meta+k') {
        const ninja = document.querySelector('ninja-keys');
        ninja.open();
        e.preventDefault();
      } else if (keyCode === 'enter' && this.isAValidEvent('enter')) {
        this.onSendReply();
        e.preventDefault();
      } else if (
        ['meta+enter', 'ctrl+enter'].includes(keyCode) &&
        this.isAValidEvent('cmd_enter')
      ) {
        this.onSendReply();
      }
         // Trigger typing status for any other keypress
    this.onTypingOn(); // Call onTypingOn to emit typing continuously
    },
    isAValidEvent(selectedKey) {
      return (
        !this.showUserMentions &&
        !this.showMentions &&
        !this.showCannedMenu &&
        !this.showVariablesMenu &&
        this.isFocused &&
        isEditorHotKeyEnabled(this.uiSettings, selectedKey)
      );
    },
    onPaste(e) {
      const data = e.clipboardData.files;
      if (!this.showRichContentEditor && data.length !== 0) {
        this.$refs.messageInput.$el.blur();
      }
      if (!data.length || !data[0]) {
        return;
      }
      data.forEach(file => {
        const { name, type, size } = file;
        this.onFileUpload({ name, type, size, file: file });
      });
    },
    toggleUserMention(currentMentionState) {
      this.showUserMentions = currentMentionState;
    },
    toggleCannedMenu(value) {
      this.showCannedMenu = value;
    },
    toggleVariablesMenu(value) {
      this.showVariablesMenu = value;
    },
    openWhatsappTemplateModal() {
      this.showWhatsAppTemplatesModal = true;
    },
    hideWhatsappTemplatesModal() {
      this.showWhatsAppTemplatesModal = false;
    },
    openEmailTemplateModal() {
      this.showEmailTemplatesModal = true;
    },
    hideEmailTemplateModal() {
      this.showEmailTemplatesModal = false;
    },
    onSendEmailTemplate({
      subject,
      body,
      attachmentSignedIds = [],
    }) {
      if (!body) return;
      const messagePayload = this.getMessagePayload(body);

      if (this.isAnEmailChannel && subject) {
        messagePayload.contentAttributes = {
          ...(messagePayload.contentAttributes || {}),
          email: {
            ...(messagePayload.contentAttributes?.email || {}),
            subject,
          },
        };
      }

      if (attachmentSignedIds.length) {
        const existing = messagePayload.files || [];
        messagePayload.files = [...existing, ...attachmentSignedIds];
      }

      this.sendMessage(messagePayload);
      this.hideEmailTemplateModal();
    },
    onClickSelfAssign() {
      const {
        account_id,
        availability_status,
        available_name,
        email,
        id,
        name,
        role,
        avatar_url,
      } = this.currentUser;
      const selfAssign = {
        account_id,
        availability_status,
        available_name,
        email,
        id,
        name,
        role,
        thumbnail: avatar_url,
      };
      this.assignedAgent = selfAssign;
    },
    confirmOnSendReply() {
      if (this.isReplyButtonDisabled) {
        return;
      }
      if (!this.showMentions) {
        let newMessage = this.message;
        if (this.isSignatureEnabledForInbox && this.messageSignature) {
          newMessage += '\n\n' + this.messageSignature;
        }

        const isOnWhatsApp =
          this.isATwilioWhatsAppChannel ||
          this.isAWhatsAppCloudChannel ||
          this.is360DialogWhatsAppChannel;
        if (isOnWhatsApp && !this.isPrivate) {
          this.sendMessageAsMultipleMessages(newMessage);
        } else {
          const messagePayload = this.getMessagePayload(newMessage);
          this.sendMessage(messagePayload);
        }

        if (!this.isPrivate) {
          this.clearEmailField();
        }

        // Reset WhatsApp typing throttle when message is sent
        this.resetWhatsappTypingThrottle();

        this.clearMessage();
        this.hideEmojiPicker();
        this.$emit('update:popoutReplyBox', false);
      }
    },
    sendMessageAsMultipleMessages(message) {
      const messages = this.getMessagePayloadForWhatsapp(message);
      messages.forEach(messagePayload => {
        this.sendMessage(messagePayload);
      });
    },
    sendMessageAnalyticsData(isPrivate) {
      // Analytics data for message signature is enabled or not in channels
      return isPrivate
        ? this.$track(CONVERSATION_EVENTS.SENT_PRIVATE_NOTE)
        : this.$track(CONVERSATION_EVENTS.SENT_MESSAGE, {
            channelType: this.channelType,
            signatureEnabled: this.sendWithSignature,
          });
    },
    async onSendReply() {
      const undefinedVariables = getUndefinedVariablesInMessage({
        message: this.message,
        variables: this.messageVariables,
      });
      if (undefinedVariables.length > 0) {
        const undefinedVariablesCount =
          undefinedVariables.length > 1 ? undefinedVariables.length : 1;
        this.undefinedVariableMessage = this.$t(
          'CONVERSATION.REPLYBOX.UNDEFINED_VARIABLES.MESSAGE',
          {
            undefinedVariablesCount,
            undefinedVariables: undefinedVariables.join(', '),
          }
        );

        const ok = await this.$refs.confirmDialog.showConfirmation();
        if (ok) {
          this.confirmOnSendReply();
        }
      } else {
        this.confirmOnSendReply();
      }
    },
    async sendMessage(messagePayload) {
      try {
        await this.$store.dispatch(
          'createPendingMessageAndSend',
          messagePayload
        );
        bus.$emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
        bus.$emit(BUS_EVENTS.MESSAGE_SENT);
        this.removeFromDraft();
        this.sendMessageAnalyticsData(messagePayload.private);
      } catch (error) {
        const errorMessage =
          error?.response?.data?.error || this.$t('CONVERSATION.MESSAGE_ERROR');
        this.showAlert(errorMessage);
      }
    },
    async onSendWhatsAppReply(messagePayload) {
      this.sendMessage({
        conversationId: this.currentChat.id,
        ...messagePayload,
      });
      this.hideWhatsappTemplatesModal();
    },
    replaceText(message) {
      const updatedMessage = replaceVariablesInMessage({
        message,
        variables: this.messageVariables,
      });
      setTimeout(() => {
        this.$track(CONVERSATION_EVENTS.INSERTED_A_CANNED_RESPONSE);
        this.message = updatedMessage;
      }, 100);
    },
    insertMessageToReplyBox(messageBody) {
      if (messageBody) {
        // Use the message body as-is without variable replacement for AI responses
        // This ensures exact copy of the AI response
        setTimeout(() => {
          this.message = messageBody;
          // Focus the input
          if (this.showRichContentEditor) {
            this.onFocus();
          } else if (this.$refs.messageInput) {
            this.$refs.messageInput.focus();
          }
        }, 150);
      }
    },
    handleSuggestionGenerated({ subject, message }) {
      // Auto-fill the message field with AI-generated content
      // Note: Subject is not used in reply box (only for new conversations)
      if (message) {
        const updatedMessage = replaceVariablesInMessage({
          message,
          variables: this.messageVariables,
        });
        setTimeout(() => {
          this.message = updatedMessage;
        }, 100);
      }
    },
    handleSendAgentContent({ content }) {
      // Set AI-generated content and send immediately (no typing effect)
      if (!content) return;
      const updatedMessage = replaceVariablesInMessage({
        message: content,
        variables: this.messageVariables,
      });
      this.message = updatedMessage;
      this.$nextTick(() => {
        this.confirmOnSendReply();
      });
    },
    setReplyMode(mode = REPLY_EDITOR_MODES.REPLY) {
      const { can_reply: canReply } = this.currentChat;
      this.$store.dispatch('draftMessages/setReplyEditorMode', {
        mode,
      });
      if (canReply || this.isAWhatsAppChannel) this.replyType = mode;
      if (this.showRichContentEditor) {
        if (this.isRecordingAudio) {
          this.toggleAudioRecorder();
        }
        return;
      }
      this.$nextTick(() => this.$refs.messageInput.focus());
    },
    clearEditorSelection() {
      this.updateEditorSelectionWith = '';
    },
    insertEmoji(emoji, selectionStart, selectionEnd) {
      const { message } = this;
      const newMessage =
        message.slice(0, selectionStart) +
        emoji +
        message.slice(selectionEnd, message.length);
      this.message = newMessage;
    },
    emojiOnClick(emoji) {
      if (this.showRichContentEditor) {
        this.updateEditorSelectionWith = emoji;
        this.onFocus();
      }
      if (!this.showRichContentEditor) {
        const { selectionStart, selectionEnd } = this.$refs.messageInput.$el;
        this.insertEmoji(emoji, selectionStart, selectionEnd);
      }
    },
    clearMessage() {
      this.message = '';
      this.attachedFiles = [];
      this.isRecordingAudio = false;
    },
    clearEmailField() {
      this.ccEmails = '';
      this.bccEmails = '';
      this.toEmails = '';
    },
    toggleEmojiPicker() {
      this.showEmojiPicker = !this.showEmojiPicker;
    },
    toggleAudioRecorder() {
      this.isRecordingAudio = !this.isRecordingAudio;
      this.isRecorderAudioStopped = !this.isRecordingAudio;
      if (!this.isRecordingAudio) {
        this.clearMessage();
        this.clearEmailField();
      }
    },
    toggleAudioRecorderPlayPause() {
      if (this.isRecordingAudio) {
        if (!this.isRecorderAudioStopped) {
          this.isRecorderAudioStopped = true;
          this.$refs.audioRecorderInput.stopAudioRecording();
        } else if (this.isRecorderAudioStopped) {
          this.$refs.audioRecorderInput.playPause();
        }
      }
    },
    hideEmojiPicker() {
      if (this.showEmojiPicker) {
        this.toggleEmojiPicker();
      }
    },
    hideMentions() {
      this.showMentions = false;
    },
    onStateProgressRecorderChanged(duration) {
      this.recordingAudioDurationText = duration;
    },
    onStateRecorderChanged(state) {
      this.recordingAudioState = state;
      if (state && 'notallowederror'.includes(state)) {
        this.toggleAudioRecorder();
      }
    },
    onFinishRecorder(file) {
      return file && this.onFileUpload(file);
    },
          // Timer to manage typing state
   startTypingTimer() {
    if (this.typingTimer) {
      clearTimeout(this.typingTimer);
    }
    this.typingTimer = setTimeout(() => {
      this.onTypingOff(); // Call onTypingOff after delay when typing stops
    }, 2000); // 2-second delay (adjust as needed)
  },

    // onTypingOn() {
    //   this.toggleTyping('on');
    //   this.startTypingTimer();
    // },
    onTypingOn() {
       if (this.isFocused) { 
         this.debouncedToggleTyping('on',this);
         this.startTypingTimer();
        }
     },
    onFocus() {
      this.isFocused = true;
      this.onTypingOn(); 
    },
    onBlur() {
       this.isFocused = false;
       this.saveDraft(this.conversationIdByRoute, this.replyType);
       this.onTypingOff(); 
     },
    onTypingOff() {
      this.debouncedToggleTyping('off',this);
    },
    toggleTyping(status) {
      const conversationId = this.currentChat.id;
      const isPrivate = this.isPrivate;

      if (!conversationId) {
        return;
      }

      if (status === 'on' && this.isFocused && this.message.trim()) {
      // console.log("status is on  event emitted",conversationId);
      bus.$emit('typing',conversationId);
      }  
      this.$store.dispatch('conversationTypingStatus/toggleTyping', {
        status,
        conversationId,
        isPrivate,
      });
    },
    
    // Start WhatsApp typing indicator
    startWhatsappTyping() {
      // Only for WhatsApp channels
      if (!this.isAWhatsAppChannel) {
        console.log('[WhatsApp Typing] Not a WhatsApp channel');
        return;
      }

      // If already active, don't send again (prevents spam during continuous typing)
      if (this.isWhatsappTypingActive) {
        console.log('[WhatsApp Typing] Already active, skipping');
        return;
      }

      // Check throttle ONLY if indicator is not active
      // This allows new indicator after previous one expires
      const now = Date.now();
      if (this.lastWhatsappTypingSent && (now - this.lastWhatsappTypingSent) < 20000) {
        const secondsRemaining = Math.ceil((20000 - (now - this.lastWhatsappTypingSent)) / 1000);
        console.log(`[WhatsApp Typing] Throttled - wait ${secondsRemaining} more seconds`);
        return;
      }

      // Send typing indicator
      this.sendWhatsappTypingIndicator();
    },

    // Stop WhatsApp typing indicator
    stopWhatsappTyping() {
      console.log('[WhatsApp Typing] Stopping typing indicator');
      this.isWhatsappTypingActive = false;
      
      // Clear the auto-stop timeout
      if (this.whatsappTypingTimeout) {
        clearTimeout(this.whatsappTypingTimeout);
        this.whatsappTypingTimeout = null;
      }
    },

    // Reset throttle when message is sent
    resetWhatsappTypingThrottle() {
      console.log('[WhatsApp Typing] Resetting throttle timer (message sent)');
      this.lastWhatsappTypingSent = null;
      this.isWhatsappTypingActive = false;
      
      if (this.whatsappTypingTimeout) {
        clearTimeout(this.whatsappTypingTimeout);
        this.whatsappTypingTimeout = null;
      }
    },

    async sendWhatsappTypingIndicator() {
      // Get the last incoming message ID from the current conversation
      const messages = this.currentChat?.messages || [];
      console.log('[WhatsApp Typing] Total messages:', messages.length);
      console.log('[WhatsApp Typing] All messages:', messages.map(m => ({
        id: m.id,
        type: m.message_type,
        source_id: m.source_id,
        content: m.content?.substring(0, 20)
      })));
      
      const lastIncomingMessage = messages
        .slice()
        .reverse()
        .find(msg => msg.message_type === 0 && msg.source_id); // message_type 0 is incoming

      if (!lastIncomingMessage || !lastIncomingMessage.source_id) {
        console.warn('[WhatsApp Typing] No incoming message with source_id found');
        console.warn('[WhatsApp Typing] Available messages:', messages.map(m => ({
          message_type: m.message_type,
          has_source_id: !!m.source_id
        })));
        return;
      }

      console.log('[WhatsApp Typing] Found incoming message:', {
        id: lastIncomingMessage.id,
        source_id: lastIncomingMessage.source_id,
        content: lastIncomingMessage.content?.substring(0, 30),
        created_at: lastIncomingMessage.created_at
      });

      try {
        await InboxesAPI.sendWhatsappTyping(this.inboxId, lastIncomingMessage.source_id);
        
        // Mark as active
        this.isWhatsappTypingActive = true;
        this.lastWhatsappTypingSent = Date.now();
        
        console.log('[WhatsApp Typing] Successfully sent typing indicator');

        // Auto-stop after 20 seconds
        // Clear previous timeout if exists
        if (this.whatsappTypingTimeout) {
          clearTimeout(this.whatsappTypingTimeout);
        }
        
        this.whatsappTypingTimeout = setTimeout(() => {
          console.log('[WhatsApp Typing] Auto-stopping after 20 seconds - ready for new indicator');
          this.isWhatsappTypingActive = false;
          this.lastWhatsappTypingSent = null; // Clear throttle to allow immediate new indicator
          this.whatsappTypingTimeout = null;
        }, 20000);
        
      } catch (error) {
        console.warn('[WhatsApp Typing] Failed to send typing indicator:', error);
        this.isWhatsappTypingActive = false;
      }
    },
    onFileUpload(file) {
      if (this.globalConfig.directUploadsEnabled) {
        this.onDirectFileUpload(file);
      } else {
        this.onIndirectFileUpload(file);
      }
    },
    // Uploads an editor-dropped/pasted image and inserts it at `pos`. The
    // `attachedFiles` entry is flagged `inline: true` so it stays out of
    // the chip row but still ships to the backend.
    onEditorImageUpload({ file, pos } = {}) {
      if (!file) return;

      const MAXIMUM_SUPPORTED_FILE_UPLOAD_SIZE = this.maxFileUploadSize;

      const wrapped = {
        name: file.name,
        type: file.type,
        size: file.size,
        file,
      };

      if (!checkFileSizeLimit(wrapped, MAXIMUM_SUPPORTED_FILE_UPLOAD_SIZE)) {
        this.showAlert(
          this.$t('CONVERSATION.FILE_SIZE_LIMIT', {
            MAXIMUM_SUPPORTED_FILE_UPLOAD_SIZE,
          })
        );
        return;
      }

      // Always use DirectUpload for inline images (regardless of the global
      // flag) so the body carries a stable signed_id URL for cid: rewrite.
      const progressEntry = this.trackDirectUpload({
        file: wrapped.file,
        name: wrapped.name,
        type: wrapped.type,
        size: wrapped.size,
      });
      const upload = new DirectUpload(
        wrapped.file,
        `/api/v1/accounts/${this.accountId}/conversations/${this.currentChat.id}/direct_uploads`,
        {
          directUploadWillCreateBlobWithXHR: xhr => {
            xhr.setRequestHeader(
              'api_access_token',
              this.currentUser.access_token
            );
          },
          directUploadWillStoreFileWithXHR: xhr => {
            this.attachProgressToXHR(progressEntry, xhr);
          },
        }
      );

      upload.create((error, blob) => {
        this.finishDirectUpload(progressEntry);
        if (error) {
          this.showAlert(error);
          return;
        }
        const src = this.buildActiveStorageBlobUrl(blob);
        this.attachInlineImage({
          file: wrapped,
          blob,
          src,
          pos,
        });
      });
    },
    onEditorNonImageDrop({ file } = {}) {
      if (!file) return;
      this.onFileUpload({
        name: file.name,
        type: file.type,
        size: file.size,
        file,
      });
    },
    // Match the editor or its immediate wrapper (so wrapper padding counts).
    isTargetInsideEditor(target) {
      if (!(target instanceof Node)) return false;
      const editor = this.$refs.messageEditor;
      if (!editor || !editor.$el) return false;
      if (editor.$el === target || editor.$el.contains(target)) return true;
      const wrapper = editor.$el.parentElement;
      if (wrapper && (wrapper === target || wrapper.contains(target))) {
        return true;
      }
      return false;
    },
    isTargetInsideReplyBox(target) {
      if (!(target instanceof Node)) return false;
      return this.$el === target || this.$el.contains(target);
    },
    // After stopImmediatePropagation, vue-upload-component never sees its
    // own drop event and the overlay stays up. Toggle it off ourselves.
    dismissUploadOverlay() {
      if (!this.isReplyBoxActive()) return;
      const panel = this.$refs.bottomPanel;
      const upload = panel && panel.$refs && panel.$refs.upload;
      if (upload && upload.dropActive) {
        upload.dropActive = false;
      }
    },
    attachGlobalListeners() {
      if (this._globalListenersAttached) return;
      this._globalListenersAttached = true;
      document.addEventListener('paste', this.onPaste);
      document.addEventListener('keydown', this.handleKeyEvents);
      // Capture phase: run before vue-upload-component's bubble handler.
      document.addEventListener('dragover', this.onCaptureDragOver, true);
      document.addEventListener('drop', this.onCaptureDrop, true);
    },
    detachGlobalListeners() {
      if (!this._globalListenersAttached) return;
      this._globalListenersAttached = false;
      document.removeEventListener('paste', this.onPaste);
      document.removeEventListener('keydown', this.handleKeyEvents);
      document.removeEventListener('dragover', this.onCaptureDragOver, true);
      document.removeEventListener('drop', this.onCaptureDrop, true);
    },
    // True only when this ReplyBox is live in the DOM. Keep-alive'd or
    // detached instances must not steal events from other dropzones.
    isReplyBoxActive() {
      return !!(this.$el && this.$el.isConnected);
    },
    // preventDefault() is required by HTML5 DnD for `drop` to fire. Only
    // opt in when the drag is inside our own DOM tree.
    onCaptureDragOver(e) {
      if (!this.isReplyBoxActive()) return;
      if (!this.showRichContentEditor) return;
      if (!this.isAnEmailChannel && !this.isOnPrivateNote) return;
      if (!this.isTargetInsideReplyBox(e.target)) return;
      const dt = e.dataTransfer;
      if (!dt || !dt.types) return;
      const hasFiles =
        (dt.types.indexOf && dt.types.indexOf('Files') !== -1) ||
        (dt.types.contains && dt.types.contains('Files'));
      if (!hasFiles) return;
      e.preventDefault();
    },
    // Routes image drops to the inline flow; non-image drops fall through
    // to vue-upload-component's regular attachment path. Scoped to this.$el.
    onCaptureDrop(e) {
      if (!this.isReplyBoxActive()) return;
      if (!this.showRichContentEditor) return;
      if (!this.isAnEmailChannel && !this.isOnPrivateNote) return;
      if (!this.isTargetInsideReplyBox(e.target)) return;
      const dt = e.dataTransfer;
      if (!dt || !dt.files || dt.files.length === 0) return;

      const files = Array.from(dt.files);
      const imageFiles = files.filter(
        f => f.type && f.type.startsWith('image/')
      );
      if (imageFiles.length === 0) return;

      e.preventDefault();
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === 'function') {
        e.stopImmediatePropagation();
      }

      let pos = null;
      try {
        const editor = this.$refs.messageEditor;
        if (editor && editor.editorView) {
          const coords = editor.editorView.posAtCoords({
            left: e.clientX,
            top: e.clientY,
          });
          pos = coords ? coords.pos : null;
        }
      } catch (err) {
        pos = null;
      }

      imageFiles.forEach(file => {
        this.onEditorImageUpload({ file, pos });
      });
      const nonImageFiles = files.filter(
        f => !(f.type && f.type.startsWith('image/'))
      );
      nonImageFiles.forEach(file => {
        this.onEditorNonImageDrop({ file });
      });

      this.dismissUploadOverlay();
    },
    buildActiveStorageBlobUrl(blob) {
      if (!blob || !blob.signed_id) return '';
      const filename = encodeURIComponent(
        blob.filename || blob.metadata?.filename || 'image'
      );
      return `/rails/active_storage/blobs/redirect/${blob.signed_id}/${filename}`;
    },
    // Drops inline attachments whose signed_id is no longer in the body
    // (i.e. the user removed the image node from the editor).
    cleanupOrphanedInlineImages(messageText) {
      if (!this.attachedFiles || this.attachedFiles.length === 0) return;
      const hasInline = this.attachedFiles.some(f => f.inline);
      if (!hasInline) return;

      const text = messageText || '';
      this.attachedFiles = this.attachedFiles.filter(item => {
        if (!item.inline) return true;
        if (!item.blobSignedId) return true;
        return text.includes(item.blobSignedId);
      });
    },
    attachInlineImage({ file, blob, src, pos }) {
      // Push synchronously so a fast Send (before FileReader resolves) still
      // includes the signed_id. `thumb` is populated async afterwards.
      const entry = {
        _clientId: this.nextAttachmentClientId(),
        currentChatId: this.currentChat.id,
        resource: blob || file,
        isPrivate: this.isPrivate,
        thumb: '',
        blobSignedId: blob ? blob.signed_id : undefined,
        inline: true,
      };
      this.attachedFiles.push(entry);

      const reader = new FileReader();
      reader.onloadend = () => {
        entry.thumb = reader.result;
      };
      reader.readAsDataURL(file.file);

      this.$nextTick(() => {
        const editor = this.$refs.messageEditor;
        if (editor && typeof editor.insertImageNode === 'function') {
          editor.insertImageNode({ src, alt: file.name || '', pos });
        }
      });
    },
    onDirectFileUpload(file) {
      const MAXIMUM_SUPPORTED_FILE_UPLOAD_SIZE = this.maxFileUploadSize;

      if (!file) {
        return;
      }
      if (checkFileSizeLimit(file, MAXIMUM_SUPPORTED_FILE_UPLOAD_SIZE)) {
        const progressEntry = this.trackDirectUpload({
          file: file.file,
          name: file.name,
          type: file.type,
          size: file.size,
        });
        const upload = new DirectUpload(
          file.file,
          `/api/v1/accounts/${this.accountId}/conversations/${this.currentChat.id}/direct_uploads`,
          {
            directUploadWillCreateBlobWithXHR: xhr => {
              xhr.setRequestHeader(
                'api_access_token',
                this.currentUser.access_token
              );
            },
            directUploadWillStoreFileWithXHR: xhr => {
              this.attachProgressToXHR(progressEntry, xhr);
            },
          }
        );

        upload.create((error, blob) => {
          this.finishDirectUpload(progressEntry);
          if (error) {
            this.showAlert(error);
          } else {
            this.attachFile({ file, blob });
          }
        });
      } else {
        this.showAlert(
          this.$t('CONVERSATION.FILE_SIZE_LIMIT', {
            MAXIMUM_SUPPORTED_FILE_UPLOAD_SIZE,
          })
        );
      }
    },
    onIndirectFileUpload(file) {
      const MAXIMUM_SUPPORTED_FILE_UPLOAD_SIZE = this.maxFileUploadSize;
      if (!file) {
        return;
      }
      if (checkFileSizeLimit(file, MAXIMUM_SUPPORTED_FILE_UPLOAD_SIZE)) {
        this.attachFile({ file });
      } else {
        this.showAlert(
          this.$t('CONVERSATION.FILE_SIZE_LIMIT', {
            MAXIMUM_SUPPORTED_FILE_UPLOAD_SIZE,
          })
        );
      }
    },
    attachFile({ blob, file }) {
      const reader = new FileReader();
      reader.readAsDataURL(file.file);
      reader.onloadend = () => {
        this.attachedFiles.push({
          _clientId: this.nextAttachmentClientId(),
          currentChatId: this.currentChat.id,
          resource: blob || file,
          isPrivate: this.isPrivate,
          thumb: reader.result,
          blobSignedId: blob ? blob.signed_id : undefined,
        });
      };
    },
    nextAttachmentClientId() {
      this._attachmentClientSeq = (this._attachmentClientSeq || 0) + 1;
      return `att-${Date.now()}-${this._attachmentClientSeq}`;
    },
    trackDirectUpload({ file, name, type, size } = {}) {
      this._uploadProgressSeq = (this._uploadProgressSeq || 0) + 1;
      const entry = {
        id: `upl-${Date.now()}-${this._uploadProgressSeq}`,
        name: name || (file && file.name) || 'file',
        type: type || (file && file.type) || '',
        size: size || (file && file.size) || 0,
        progress: 0,
        thumb: '',
      };
      if (entry.type && entry.type.startsWith('image/') && file) {
        try {
          entry.thumb = URL.createObjectURL(file);
        } catch (e) {
          entry.thumb = '';
        }
      }
      this.activeUploads.push(entry);
      return entry;
    },
    attachProgressToXHR(entry, xhr) {
      if (!entry || !xhr || !xhr.upload) return;
      xhr.upload.addEventListener('progress', event => {
        if (!event.lengthComputable) return;
        const percent = Math.round((event.loaded / event.total) * 100);
        entry.progress = percent > 100 ? 100 : percent;
      });
    },
    finishDirectUpload(entry) {
      if (!entry) return;
      this.activeUploads = this.activeUploads.filter(u => u.id !== entry.id);
      if (entry.thumb && entry.thumb.indexOf('blob:') === 0) {
        try {
          URL.revokeObjectURL(entry.thumb);
        } catch (e) {
          // ignore
        }
      }
    },
    resetAttachmentsOnConversationChange() {
      this.attachedFiles = [];
      this.activeUploads.forEach(u => {
        if (u.thumb && u.thumb.indexOf('blob:') === 0) {
          try {
            URL.revokeObjectURL(u.thumb);
          } catch (e) {
            // ignore
          }
        }
      });
      this.activeUploads = [];
      this.hideEmojiPicker();
    },
    removeAttachment(itemIndex) {
      // `itemIndex` indexes the filtered visibleAttachedFiles view.
      // Resolve to a stable client id to survive array re-creation.
      const target = this.visibleAttachedFiles[itemIndex];
      if (!target) return;
      const targetId = target._clientId;
      this.attachedFiles = this.attachedFiles.filter(
        item => targetId ? item._clientId !== targetId : item !== target
      );
    },
    getMessagePayloadForWhatsapp(message) {
      const multipleMessagePayload = [];

      if (this.attachedFiles && this.attachedFiles.length) {
        let caption = message;
        this.attachedFiles.forEach(attachment => {
          const attachedFile = attachment.blobSignedId
            ? attachment.blobSignedId
            : attachment.resource.file;
          const attachmentPayload = {
            conversationId: this.currentChat.id,
            files: [attachedFile],
            private: false,
            message: caption,
            sender: this.sender,
          };
          multipleMessagePayload.push(attachmentPayload);
          caption = '';
        });
      } else {
        const messagePayload = {
          conversationId: this.currentChat.id,
          message,
          private: false,
          sender: this.sender,
        };
        multipleMessagePayload.push(messagePayload);
      }

      return multipleMessagePayload;
    },
    getMessagePayload(message) {
      const messagePayload = {
        conversationId: this.currentChat.id,
        message,
        private: this.isPrivate,
        sender: this.sender,
      };

      if (this.inReplyTo) {
        messagePayload.contentAttributes = { in_reply_to: this.inReplyTo };
      }

      if (this.attachedFiles && this.attachedFiles.length) {
        messagePayload.files = [];
        this.attachedFiles.forEach(attachment => {
          // Prefer signed_id (inline images always have it); fall back to
          // the raw File for the legacy indirect-upload path.
          if (attachment.blobSignedId) {
            messagePayload.files.push(attachment.blobSignedId);
          } else {
            messagePayload.files.push(attachment.resource.file);
          }
        });
      }

      if (this.ccEmails && !this.isOnPrivateNote) {
        messagePayload.ccEmails = this.ccEmails;
      }

      if (this.bccEmails && !this.isOnPrivateNote) {
        messagePayload.bccEmails = this.bccEmails;
      }

      if (this.toEmails && !this.isOnPrivateNote) {
        messagePayload.toEmails = this.toEmails;
      }

      return messagePayload;
    },
    setCcEmails(value) {
      this.bccEmails = value.bccEmails;
      this.ccEmails = value.ccEmails;
    },
    setCCAndToEmailsFromLastChat() {
      if (!this.lastEmail) return;

      const {
        content_attributes: { email: emailAttributes = {} },
      } = this.lastEmail;

      // Retrieve the email of the current conversation's sender
      const conversationContact = this.currentChat?.meta?.sender?.email || '';
      let cc = emailAttributes.cc ? [...emailAttributes.cc] : [];
      let to = [];

      // there might be a situation where the current conversation will include a message from a third person,
      // and the current conversation contact is in CC.
      // This is an edge-case, reported here: CW-1511 [ONLY FOR INTERNAL REFERENCE]
      // So we remove the current conversation contact's email from the CC list if present
      if (cc.includes(conversationContact)) {
        cc = cc.filter(email => email !== conversationContact);
      }

      // If the last incoming message sender is different from the conversation contact, add them to the "to"
      // and add the conversation contact to the CC
      if (!emailAttributes.from.includes(conversationContact)) {
        to.push(...emailAttributes.from);
        cc.push(conversationContact);
      }

      // Remove the conversation contact's email from the BCC list if present
      let bcc = (emailAttributes.bcc || []).filter(
        email => email !== conversationContact
      );

      // Ensure only unique email addresses are in the CC list
      bcc = [...new Set(bcc)];
      cc = [...new Set(cc)];
      to = [...new Set(to)];

      this.ccEmails = cc.join(', ');
      this.bccEmails = bcc.join(', ');
      this.toEmails = to.join(', ');
    },
  },
};
</script>

<style lang="scss" scoped>
.send-button {
  @apply mb-0;
}

.banner--self-assign {
  @apply py-2;
}

.message-signature-wrap {
  @apply my-0 mx-4 px-1 flex max-h-[8vh] items-baseline justify-between hover:bg-slate-25 dark:hover:bg-slate-800 border border-dashed border-slate-100 dark:border-slate-700 rounded-sm overflow-auto;
}

.message-signature {
  @apply w-fit m-0;
}

.attachment-preview-box {
  @apply bg-transparent py-0 px-4;
}

.upload-progress-box {
  @apply flex flex-col gap-1 mt-2 mx-4 max-h-[10rem] overflow-auto;
}

.upload-progress-item {
  @apply flex items-center gap-2 p-1 bg-slate-50 dark:bg-slate-800 rounded-md w-[15rem];
}

.upload-thumb-wrap {
  @apply w-6 h-6 flex items-center justify-center flex-shrink-0;
}

.upload-thumb {
  @apply w-6 h-6 object-cover rounded-sm;
}

.upload-thumb-icon {
  @apply text-base;
}

.upload-meta {
  @apply flex-1 min-w-0 flex flex-col;
}

.upload-name {
  @apply text-xs font-medium text-slate-800 dark:text-slate-100 truncate;
}

.upload-bar {
  @apply mt-1 h-1 w-full bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden;
}

.upload-bar-fill {
  @apply h-full bg-woot-500 rounded-full transition-all duration-150 ease-out;
}

.upload-percent {
  @apply text-xs text-slate-500 dark:text-slate-300 w-9 text-right tabular-nums flex-shrink-0;
}

.reply-box {
  @apply border-t border-slate-50 dark:border-slate-700 bg-white dark:bg-slate-900;

  &.is-private {
    @apply bg-yellow-50 dark:bg-yellow-200;
  }
}
.send-button {
  @apply mb-0;
}

.reply-box__top {
  @apply relative py-0 px-4 -mt-px border-t border-solid border-slate-50 dark:border-slate-700;
}

.emoji-dialog {
  @apply top-[unset] -bottom-10 -left-80 right-[unset];

  &::before {
    transform: rotate(270deg);
    filter: drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.08));
    @apply -right-4 bottom-2 rtl:right-0 rtl:-left-4;
  }
}

.emoji-dialog--rtl {
  @apply left-[unset] -right-80;
  &::before {
    transform: rotate(90deg);
    filter: drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.08));
  }
}

.emoji-dialog--expanded {
  @apply left-[unset] bottom-0 absolute z-[100];

  &::before {
    transform: rotate(0deg);
    @apply left-1 -bottom-2;
  }
}
.message-signature {
  @apply mb-0;

  :deep p:last-child {
    @apply mb-0;
  }
}

.normal-editor__canned-box {
  width: calc(100% - 2 * var(--space-normal));
  left: var(--space-normal);
}
.z-0{
  z-index:0;
}
</style>
